import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { useAlert } from 'contexts/AlertContext';
import './TravelRecommendations.css';

interface RecommendedPlace {
    id: string;
    name: string;
    description: string;
    category: string;
    rating?: number;
    priceRange?: string;
    imageUrl?: string;
    tags: string[];
}

interface SelectedPlace {
    id: string;
    name: string;
    description: string;
    category: string;
    address?: string;
    lat?: number;
    lng?: number;
}

// 임시 추천 데이터 (나중에 AI/API로 대체)
const MOCK_RECOMMENDATIONS: Record<string, RecommendedPlace[]> = {
    attraction: [
        { id: 'a1', name: '센소지', description: '도쿄 최고의 사찰', category: 'attraction', rating: 4.5, tags: ['사찰', '역사', '포토스팟'] },
        { id: 'a2', name: '도쿄타워', description: '도쿄의 랜드마크', category: 'attraction', rating: 4.3, tags: ['전망대', '야경', '랜드마크'] },
        { id: 'a3', name: '시부야 스크램블', description: '세계 최대 횡단보도', category: 'attraction', rating: 4.4, tags: ['도심', '포토스팟'] },
        { id: 'a4', name: '메이지신궁', description: '도심 속 신성한 공간', category: 'attraction', rating: 4.6, tags: ['신사', '자연', '힐링'] },
        { id: 'a5', name: '아사쿠사', description: '전통 상점가 탐방', category: 'attraction', rating: 4.4, tags: ['쇼핑', '전통', '먹거리'] },
    ],
    restaurant: [
        { id: 'r1', name: '이치란 라멘', description: '돈코츠 라멘 맛집', category: 'restaurant', rating: 4.5, priceRange: '$$', tags: ['라멘', '혼밥'] },
        { id: 'r2', name: '스시 다이', description: '츠키지 인기 스시', category: 'restaurant', rating: 4.7, priceRange: '$$$', tags: ['스시', '신선'] },
        { id: 'r3', name: '규카츠 모토무라', description: '규카츠 원조 맛집', category: 'restaurant', rating: 4.6, priceRange: '$$', tags: ['규카츠', '웨이팅'] },
        { id: 'r4', name: '아후리 라멘', description: '유자 소금 라멘', category: 'restaurant', rating: 4.4, priceRange: '$$', tags: ['라멘', '깔끔'] },
    ],
    cafe: [
        { id: 'c1', name: '블루보틀 시부야', description: '미니멀 감성 카페', category: 'cafe', rating: 4.3, priceRange: '$$', tags: ['커피', '인테리어'] },
        { id: 'c2', name: '% 아라비카', description: '교토 본점 감성', category: 'cafe', rating: 4.5, priceRange: '$$', tags: ['커피', '포토스팟'] },
        { id: 'c3', name: '스트리머 커피', description: '라떼아트 명소', category: 'cafe', rating: 4.4, priceRange: '$$', tags: ['라떼', '힙플레이스'] },
    ],
    hotel: [
        { id: 'h1', name: '신주쿠 그랑벨', description: '신주쿠역 도보 5분', category: 'hotel', rating: 4.2, priceRange: '$$', tags: ['역세권', '깨끗'] },
        { id: 'h2', name: '도쿄 스테이션 호텔', description: '도쿄역 직결', category: 'hotel', rating: 4.8, priceRange: '$$$$', tags: ['럭셔리', '역사'] },
        { id: 'h3', name: '시부야 스트림 호텔', description: '시부야 신상 호텔', category: 'hotel', rating: 4.5, priceRange: '$$$', tags: ['신상', '시부야'] },
    ],
};

const CATEGORY_INFO: Record<string, { icon: string; title: string }> = {
    attraction: { icon: '🏛️', title: '관광지' },
    restaurant: { icon: '🍽️', title: '맛집' },
    cafe: { icon: '☕', title: '카페' },
    hotel: { icon: '🏨', title: '숙소' },
};

export const TravelRecommendations = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();

    const { meetingId, category, destination } = location.state || {};
    const categoryInfo = CATEGORY_INFO[category] || { icon: '📍', title: '장소' };

    const [selectedPlaces, setSelectedPlaces] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // 추천 장소 목록 (실제로는 API에서 가져옴)
    const recommendations = MOCK_RECOMMENDATIONS[category] || [];

    const filteredRecommendations = searchQuery
        ? recommendations.filter(place =>
            place.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            place.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            place.tags.some(tag => tag.includes(searchQuery))
        )
        : recommendations;

    const handleTogglePlace = (placeId: string) => {
        setSelectedPlaces(prev =>
            prev.includes(placeId)
                ? prev.filter(id => id !== placeId)
                : [...prev, placeId]
        );
    };

    const handleSearchPlace = () => {
        // 직접 장소 검색 화면으로 이동
        navigate('/meeting/place-search', {
            state: {
                returnTo: '/meeting/travel-recommendations',
                meetingId,
                category,
                destination,
                isSubPlaceSearch: true
            }
        });
    };

    const handleAddPlaces = async () => {
        if (selectedPlaces.length === 0) {
            await showAlert({ message: '추가할 장소를 선택해주세요.' });
            return;
        }

        setIsLoading(true);

        try {
            // 선택된 장소들을 모임의 서브 장소로 등록
            const placesToAdd = recommendations.filter(p => selectedPlaces.includes(p.id));

            // TODO: API 호출로 서브 장소 등록
            // await MeetingRepository.addSubPlaces(meetingId, placesToAdd);

            console.log('Adding places to meeting:', meetingId, placesToAdd);

            await showAlert({ message: `${placesToAdd.length}개의 장소가 추가되었습니다.` });

            // 완료 화면으로 돌아가기
            navigate(-1);
        } catch (error) {
            console.error('장소 추가 실패:', error);
            await showAlert({ message: '장소 추가에 실패했습니다.' });
        } finally {
            setIsLoading(false);
        }
    };

    const renderRating = (rating?: number) => {
        if (!rating) return null;
        return (
            <span className="place-rating">
                ⭐ {rating.toFixed(1)}
            </span>
        );
    };

    return (
        <div className="travel-recommend-wrapper">
            <Header title={`${categoryInfo.title} 추가`} isSubPage />

            <div className="travel-recommend-content">
                {/* Header Info */}
                <div className="recommend-info-header">
                    <span className="info-emoji">{categoryInfo.icon}</span>
                    <div className="info-text">
                        <h2 className="info-title">{destination} {categoryInfo.title} 추천</h2>
                        <p className="info-desc">여행에 추가할 장소를 선택하세요</p>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="recommend-search">
                    <input
                        type="text"
                        className="recommend-search-input"
                        placeholder="장소 검색..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="search-direct-btn" onClick={handleSearchPlace}>
                        + 직접 검색
                    </button>
                </div>

                {/* Selected Count */}
                {selectedPlaces.length > 0 && (
                    <div className="selected-count-bar">
                        <span className="selected-count">{selectedPlaces.length}개 선택됨</span>
                        <button
                            className="clear-selection-btn"
                            onClick={() => setSelectedPlaces([])}
                        >
                            선택 해제
                        </button>
                    </div>
                )}

                {/* Recommendations List */}
                <div className="recommend-places-list">
                    {filteredRecommendations.map((place) => (
                        <div
                            key={place.id}
                            className={`recommend-place-card ${selectedPlaces.includes(place.id) ? 'selected' : ''}`}
                            onClick={() => handleTogglePlace(place.id)}
                        >
                            <div className="place-select-indicator">
                                <div className="select-checkbox">
                                    {selectedPlaces.includes(place.id) && <span>✓</span>}
                                </div>
                            </div>

                            <div className="place-main-content">
                                <div className="place-header">
                                    <h3 className="place-name">{place.name}</h3>
                                    {renderRating(place.rating)}
                                </div>
                                <p className="place-description">{place.description}</p>
                                <div className="place-tags">
                                    {place.tags.map((tag, idx) => (
                                        <span key={idx} className="place-tag">#{tag}</span>
                                    ))}
                                    {place.priceRange && (
                                        <span className="place-price">{place.priceRange}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {filteredRecommendations.length === 0 && (
                        <div className="no-results">
                            <span className="no-results-icon">🔍</span>
                            <p className="no-results-text">검색 결과가 없습니다</p>
                            <button className="search-direct-btn-large" onClick={handleSearchPlace}>
                                직접 장소 검색하기
                            </button>
                        </div>
                    )}
                </div>

                {/* AI Hint */}
                <div className="ai-recommend-hint">
                    <span className="ai-icon">🤖</span>
                    <span className="ai-text">AI가 {destination} 인기 장소를 추천해드렸어요</span>
                </div>
            </div>

            {/* Footer */}
            <div className="travel-recommend-footer">
                <Button
                    text={isLoading ? "추가 중..." : `${selectedPlaces.length}개 장소 추가하기`}
                    disabled={selectedPlaces.length === 0 || isLoading}
                    onPress={handleAddPlaces}
                />
            </div>
        </div>
    );
};
